import React from 'react';
import { useQuery } from 'react-query';
import { ChartData, dataExistsChecker, handleSales } from '../utils/reusable-func';
import api from '../../../../components/api';

function useRenderChartClient(currentClient: {value: number, label: string} | null, currentVM: {value: number, label: string} | null) {
  /* Handle current view of chart
  * 0. Today
  * 1. This Week
  * 2. This Month
  * 3. 3 Months
  * 4. 6 Months
  * 5. 1 Year
  */
  const [currentView, setCurrentView] = React.useState<number>(1);

  /*
  * Get Chart Data
  * Need to be run with enabled after finish fetching first data for handling error from Back End
  * 1. Client Data
  * 2. VM Data
  */
  // 1, Client Data
  // 1.1. Today Data
  const { data: today, isLoading: todayLoad } = useQuery(
    ['hourly', 'client', 'analytics', currentClient?.value, 1],
    () => api.analytics.client.hourly(currentClient!.value, 1),
    {
      enabled: !!currentClient,
    },
  );
  // 1.2. Week Data
  const { data: week, isLoading: weekLoad } = useQuery(
    ['hourly', 'client', 'analytics', currentClient?.value, 7],
    () => api.analytics.client.hourly(currentClient!.value, 7),
    {
      enabled: !!currentClient && !!today,
    },
  );
  // 1.3. Month Data
  const { data: month, isLoading: monthLoad } = useQuery(
    ['daily', 'client', 'analytics', currentClient?.value, 30],
    () => api.analytics.client.daily(currentClient!.value, 30),
    {
      enabled: !!currentClient && !!week,
    },
  );
  // 1.4. Three Month Data
  const { data: threeMonth, isLoading: threeMonthLoad } = useQuery(
    ['daily', 'client', 'analytics', currentClient?.value, 90],
    () => api.analytics.client.daily(currentClient!.value, 90),
    {
      enabled: !!currentClient && !!month,
    },
  );
  // 1.5. Six Month Data
  const { data: sixMonth, isLoading: sixMonthLoad } = useQuery(
    ['weekly', 'client', 'analytics', currentClient?.value, 6],
    () => api.analytics.client.weekly(currentClient!.value, 6),
    {
      enabled: !!currentClient && !!threeMonth,
    },
  );
  // 1.6. Year Data
  const { data: year, isLoading: yearLoad } = useQuery(
    ['monthly', 'client', 'analytics', currentClient?.value, 12],
    () => api.analytics.client.monthly(currentClient!.value, 12),
    {
      enabled: !!currentClient && !!sixMonth,
    },
  );

  // 2, VM Data
  // 2.1. Today Data
  const { data: todayVm, isLoading: todayVmLoad } = useQuery(
    ['hourly', 'vm', 'analytics', currentVM?.value, 1],
    () => api.analytics.vm.hourly(currentVM!.value, 1),
    {
      enabled: !!currentVM,
    },
  );
  // 2.2. Week Data
  const { data: weekVm, isLoading: weekVmLoad } = useQuery(
    ['hourly', 'vm', 'analytics', currentVM?.value, 7],
    () => api.analytics.vm.hourly(currentVM!.value, 7),
    {
      enabled: !!currentVM && !!todayVm,
    },
  );
  // 2.3. Month Data
  const { data: monthVm, isLoading: monthVmLoad } = useQuery(
    ['daily', 'vm', 'analytics', currentVM?.value, 30],
    () => api.analytics.vm.daily(currentVM!.value, 30),
    {
      enabled: !!currentVM && !!weekVm,
    },
  );
  // 2.4. Three Month Data
  const { data: threeMonthVm, isLoading: threeMonthVmLoad } = useQuery(
    ['daily', 'vm', 'analytics', currentVM?.value, 90],
    () => api.analytics.vm.daily(currentVM!.value, 90),
    {
      enabled: !!currentVM && !!monthVm,
    },
  );
  // 2.5. Six Month Data
  const { data: sixMonthVm, isLoading: sixMonthVmLoad } = useQuery(
    ['weekly', 'vm', 'analytics', currentVM?.value, 6],
    () => api.analytics.vm.weekly(currentVM!.value, 6),
    {
      enabled: !!currentVM && !!threeMonthVm,
    },
  );
  // 3.6. Year Data
  const { data: yearVm, isLoading: yearVmLoad } = useQuery(
    ['monthly', 'vm', 'analytics', currentVM?.value, 12],
    () => api.analytics.vm.monthly(currentVM!.value, 12),
    {
      enabled: !!currentVM && !!sixMonthVm,
    },
  );

  // Data Management
  const salesData: ChartData | undefined = React.useMemo(() => {
    if (!currentVM) {
      return handleSales({
        today: today?.data?.data,
        oneWeek: week?.data?.data,
        oneMonth: month?.data?.data,
        threeMonth: threeMonth?.data?.data,
        sixMonth: sixMonth?.data?.data,
        year: year?.data?.data,
      }, currentView);
    }
    return handleSales({
      today: todayVm?.data?.data,
      oneWeek: weekVm?.data?.data,
      oneMonth: monthVm?.data?.data,
      threeMonth: threeMonthVm?.data?.data,
      sixMonth: sixMonthVm?.data?.data,
      year: yearVm?.data?.data,
    }, currentView);
  }, [year, yearVm, currentView]);

  // Checking data exists or not for rendering chart
  const isDataExists: boolean = React.useMemo(() => dataExistsChecker({
    today: today?.data?.data,
    oneWeek: week?.data?.data,
    oneMonth: month?.data?.data,
    threeMonth: threeMonth?.data?.data,
    sixMonth: sixMonth?.data?.data,
    year: year?.data?.data,
  }), [year]);

  // Checking API Loading status
  const isLoading: boolean = React.useMemo(
    () => todayLoad || weekLoad || monthLoad || threeMonthLoad || sixMonthLoad || yearLoad || todayVmLoad || weekVmLoad || monthVmLoad || threeMonthVmLoad || sixMonthVmLoad || yearVmLoad,
    [todayLoad, weekLoad, monthLoad, threeMonthLoad, sixMonthLoad, yearLoad, todayVmLoad, weekVmLoad, monthVmLoad, threeMonthVmLoad, sixMonthVmLoad, yearVmLoad],
  );

  return {
    currentView,
    setCurrentView,
    salesData,
    isLoading,
    isDataExists,
  };
}

export default useRenderChartClient;
